import './App.scss';
import { useState, useEffect, useMemo } from 'react';
import { FaBars } from 'react-icons/fa';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Markets } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import SideMenu from '../SideMenu/SideMenu';
import RestrictedRoute from '../RestrictedRoute/RestrictedRoute';
import HomePage from '../../pages/HomePage/HomePage';
import VehicleListPage from '../../pages/VehicleListPage/VehicleListPage';
import OfferListPage from '../../pages/OfferListPage/OfferListPage';
import DetailsPage from '../../pages/DetailsPage/DetailsPage';
import NotFound from '../../pages/NotFound/NotFound';
import NotAuthorized from '../../pages/NotAuthorized/NotAuthorized';
import TopLoadingBar from '../TopLoadingBar/TopLoadingBar';
import AutobizPage from '../../pages/AutobizPage/AutobizPage';
import ToastContainer from '../ToastContainer/ToastContainer';
import DetailsFormPage from '../../pages/DetailsFormPage/DetailsFormPage';
import DealerListPage from '../../pages/DealerListPage/DealerListPage';
import UserListPage from '../../pages/UserListPage/UserListPage';
import DealerFormPage from '../../pages/DealerFormPage/DealerFormPage';
import UserFormPage from '../../pages/UserFormPage/UserFormPage';
import OfferEditPage from '../../pages/OfferEditPage/OfferEditPage';
import MyStockPage from '../../pages/MyStockPage/MyStockPage';
import VehiclePromoListPage from '../../pages/VehiclePromoListPage/VehiclePromoListPage';
import QuickoVehiclesPage from '../../pages/QuickoVehiclesPage/QuickoVehiclesPage';
import WishlistPage from '../../pages/WishlistPage/WishlistPage';
import AreaAcademy from '../../pages/Areas/AreaAcademyPage/AreaAcademy';
import AreaMarketing from '../../pages/Areas/AreaMarketingPage/AreaMarketing';
import EmbedFile from '../../pages/EmbedFilePage/EmbedFile';
import QuotationList from '../../pages/QuotationListPage/QuotationList';
import TrackingConsent from '../../components/TrackingConsent/TrackingConsent';
import AccountListPage from '../../pages/AccountListPage/AccountListPage';
import AccountFormPage from '../../pages/AccountFormPage/AccountFormPage';
import Opportunity from '../../pages/OpportunityPage/OpportunityPage';
import MyCompaniesPage from '../../pages/MyCompaniesPage/MyCompaniesPage';
import CreateOpportunityPage from '../../pages/CreateOpportunityPage/CreateOpportunityPage';
import VehicleOfferPage from '../../pages/VehicleOfferPage/VehicleOfferPage';
import AutorevolWizard from '../../pages/AutorevolWizard/AutorevolWizard';
import MyFleetPage from '../../pages/MyFleetPage/MyFleetPage';
import LeadsPage from '../../pages/Leads/LeadsPage';
import LeadConvertPage from '../../pages/LeadConvert/LeadConvertPage';
import { isMarket } from '../../utils/markets';
import { flushPendingLoginEvent } from '../../utils/trackingGA4';
import OpportunityListPage from 'src/pages/OpportunityListPage/OpportunityListPage';
import { PreprodAlert } from '../PreprodAlert/PreprodAlert';
import { canViewReportersPage, isPreprod } from 'src/utils';
import { RoleEnum } from 'src/api';
import ReporterListPage from '../../pages/ReporterListPage/ReporterListPage';
import { ReportListPage } from 'src/pages/ReportListPage/ReportListPage';
import ReporterDetailsPage from 'src/pages/ReporterDetailsPage/ReporterDetailsPage';
import StellantisQuoteRequestsPage from 'src/pages/StellantisQuoteRequestsPage/StellantisQuoteRequestsPage';
import StellantisQuotesPage from 'src/pages/StellantisQuotesPage/StellantisQuotesPage';
import StellantisQuotesHistoryPage from 'src/pages/StellantisQuotesHistoryPage/StellantisQuotesHistoryPage';
import StellantisOpportunityWizardPage from 'src/pages/StellantisOpportunityWizardPage/StellantisOpportunityWizardPage';
import StellantisQuotesSegnalatorePage from 'src/pages/StellantisQuotesSegnalatorePage/StellantisQuotesSegnalatorePage';
import CarConfiguratorMotornetListPage from 'src/pages/CarConfiguratorMotornetListPage/CarConfiguratorMotornetListPage';
import MotornetConfiguratorPage from 'src/components/CarConfiguratorMotornet/ConfiguratorPage';
import MyCarConfigurationsPage from 'src/pages/MyCarConfigurationsPage/MyCarConfigurationsPage';
import QuotationsHistoryPage from 'src/pages/QuotationsHistoryPage/QuotationsHistoryPage';
import VehicleAssociationPage from 'src/pages/VehicleAssociationPage/VehicleAssociationPage';
import MotornetCreateVehiclePage from 'src/motornet/MotornetDetailsFormPage/MotornetCreateVehiclePage';
import MotornetImportPage from 'src/motornet/import/MotornetImportPage';
import ResolveVehiclePage from 'src/motornet/ResolveVehiclePage/ResolveVehiclePage';
import ContactsPage from 'src/pages/ContactsPage/ContactsPage';

function App() {
	const [toggled, setToggled] = useState(false);

	const location = useLocation();
	const currentURL = location.pathname;
	const { userRole, isBrandedHmp, userReferencePartnerList} = useAuth();

	const handleToggleSidebar = (value) => {
		setToggled(value);
	};

	useEffect(() => {
		flushPendingLoginEvent();
	}, []);

	useEffect(() => {
		const rootElement = document.body;
		if (isBrandedHmp) {
			rootElement.classList.add('maserati-theme');
		} else {
			rootElement.classList.remove('maserati-theme');
		}
	}, [isBrandedHmp]);

	const shouldShowPaddingAndButton = !['/privacy', '/wizard'].some(path => currentURL.includes(path));

	const canViewMyReporter = useMemo(() => {
		return canViewReportersPage(userRole, userReferencePartnerList);
	}, [userRole, userReferencePartnerList])

	const isSegnalatore = userRole === RoleEnum.SEGNALATORE;

	return (
		<div className="app">
			<TopLoadingBar />
			<ToastContainer />
			{!currentURL.includes('/privacy') ?
				<div>
					<TrackingConsent />
					<SideMenu toggled={toggled} handleToggleSidebar={handleToggleSidebar} />
				</div>
				: ""}

			<main className={shouldShowPaddingAndButton ? "p-2 p-lg-5 p-md-4" : ""}>
				{shouldShowPaddingAndButton && (
					<button
						type="button"
						onClick={() => handleToggleSidebar(!toggled)}
						className="btn btn-sidemenu-toggle mb-3"
					>
						<FaBars />
					</button>
				)}
				{isPreprod() ? <PreprodAlert /> : null}
				<Routes>
					<Route path="/" element={[RoleEnum.FLEET_MANAGER, RoleEnum.SEGNALATORE].includes(userRole) ? <Navigate replace to="/available-vehicles" /> : <HomePage />} />
					<Route path="/available-vehicles" element={<VehicleListPage />} />
					<Route path="/my-companies" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER]}><MyCompaniesPage /></RestrictedRoute>} />
					<Route path="/my-accounts" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER, RoleEnum.SEGNALATORE]}><AccountListPage /></RestrictedRoute>} />
					<Route path="/my-accounts/:id" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER, RoleEnum.SEGNALATORE]}><AccountFormPage /></RestrictedRoute>} />
					<Route path="/my-opportunities" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER]}><OpportunityListPage /></RestrictedRoute>} />
					<Route path="/opportunities/new" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER]}><CreateOpportunityPage /></RestrictedRoute>} />
					<Route path="/opportunities/:id" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER, RoleEnum.SEGNALATORE]}><Opportunity /></RestrictedRoute>} />
					<Route path="/opportunities/:id/edit" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER]}><CreateOpportunityPage /></RestrictedRoute>} />
					<Route path="/promo-vehicles" element={<VehiclePromoListPage />} />
					<Route path="/quicko-vehicles" element={<QuickoVehiclesPage />} />
					<Route path="/details/:id" element={<DetailsPage />} />
					<Route path="/details/:id/edit" element={<DetailsFormPage />} />
					<Route path="/details/:id/clone" element={<DetailsFormPage />} />
					<Route path="/my-stock" element={<MyStockPage />} />
					<Route path="/my-fleet" element={<MyFleetPage />} />
					<Route path="/wishlist/" element={<WishlistPage />} />
					<Route path="/wishlist/:username" element={<WishlistPage />} />
					<Route path="/vehicle-offer" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.AREA_MANAGER]}><VehicleOfferPage /></RestrictedRoute>} />
					<Route path="/offers" element={<OfferListPage />} />
					<Route path="/offers/:id" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER]}><OfferEditPage /></RestrictedRoute>} />
					<Route path="/manage-dealers" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN]}><DealerListPage /></RestrictedRoute>} />
					<Route path="/manage-dealers/:id" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN]}><DealerFormPage /></RestrictedRoute>} />
					<Route path="/manage-users" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER]}><UserListPage /></RestrictedRoute>} />
					<Route path="/manage-users/:username" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER]}><UserFormPage /></RestrictedRoute>} />
					{/* <Route path="/import" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.ADMIN]}><ImportPage /></RestrictedRoute>} /> */}
					<Route path="/autobiz" element={<RestrictedRoute to={[RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER]}><AutobizPage /></RestrictedRoute>} />
					<Route path="/personal-area/:username" element={<UserFormPage />} />
					<Route path="/area-academy" element={<AreaAcademy />} />
					<Route path="/area-academy/:id" element={<AreaAcademy />} />
					<Route path="/area-marketing" element={<AreaMarketing />} />
					<Route path="/area-marketing/:id" element={<AreaMarketing />} />
					<Route path="/area-academy/file/:title" element={<EmbedFile />} />
					<Route path="/area-marketing/file/:title" element={<EmbedFile />} />
					<Route path="/quotation-request-list" element={<RestrictedRoute to={[RoleEnum.FLEET_MANAGER]}><QuotationList /></RestrictedRoute>} />
					<Route path="/privacy-dealer/:title" element={<EmbedFile />} />
					<Route path="/privacy-dipendenti/:title" element={<EmbedFile />} />
					<Route path="/privacy-dealer/" element={<EmbedFile />} />
					<Route path="/privacy-dipendenti/" element={<EmbedFile />} />
					{isMarket(Markets.it) && (process.env.REACT_APP_WIZARD_ENABLED === '1') && !isBrandedHmp && (
						<Route path="/wizard" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.AREA_MANAGER]}><AutorevolWizard /></RestrictedRoute>} />
					)}
					{/* {isMarket(Markets.it) && (process.env.REACT_APP_WIZARD_ENABLED === '1') && !isBrandedHmp && (
						<Route path="/wizard" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.ADMIN, RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL]}><OldAutorevolWizard /></RestrictedRoute>} />
					)} */}
					<Route path="not-authorized" element={<NotAuthorized />} />
					<Route path="*" element={<NotFound />} />
					{/* <Route path="/leads" element={<RestrictedRoute to={[]}><LeadsPage /></RestrictedRoute>} /> */}
					{/* <Route path="/leads/convert-lead" element={<RestrictedRoute to={[]}><LeadConvertPage /></RestrictedRoute>} /> */}
					<Route path="/leads" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.AREA_MANAGER]}><LeadsPage /></RestrictedRoute>} />
					<Route path="/leads/convert-lead" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.AREA_MANAGER]}><LeadConvertPage /></RestrictedRoute>} />
					{/* <Route path="/leads/:id" element={<LeadDetail />} /> */}
					{isMarket(Markets.it) && (
						<>
							<Route path="/stellantis-quote-requests" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER]}><StellantisQuoteRequestsPage /></RestrictedRoute>} />
							{/* <Route path="/stellantis-quotes" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.SALES, RoleEnum.SALES_DEALER]}><StellantisQuotesPage /></RestrictedRoute>} /> */}
							<Route path="/stellantis-quotes" element={
								isSegnalatore
								? <Navigate to="/stellantis-quotes-segnalatore" replace />
								: <RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.SALES, RoleEnum.SALES_DEALER]}><StellantisQuotesPage /></RestrictedRoute>
							} />
							<Route path="/stellantis-quotes-history" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER]}><StellantisQuotesHistoryPage /></RestrictedRoute>} />
							<Route path="/stellantis-quotes/:id/opportunity-wizard" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.SALES, RoleEnum.SALES_DEALER]}><StellantisOpportunityWizardPage /></RestrictedRoute>} />
							<Route path="/stellantis-quotes-segnalatore" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.SEGNALATORE]}><StellantisQuotesSegnalatorePage /></RestrictedRoute>} />
							<Route path="/car-configurator-list" element={<RestrictedRoute to={[RoleEnum.MASTER]}><CarConfiguratorMotornetListPage /></RestrictedRoute>} />
							<Route path="/vehicle-association" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.BACKOFFICE]}><VehicleAssociationPage /></RestrictedRoute>} />
							<Route
								path="/car-configurator"
								element={
									<RestrictedRoute to={[RoleEnum.MASTER]}>
										<MotornetConfiguratorPage />
									</RestrictedRoute>
								}
							/>
							<Route
								path="/my-car-configurations"
								element={
									<RestrictedRoute to={[RoleEnum.MASTER]}>
										<MyCarConfigurationsPage />
									</RestrictedRoute>
								}
							/>
						</>
					)}
					{canViewMyReporter && (
						<>
							<Route path="/my-reporters" element={<RestrictedRoute to={[RoleEnum.MASTER].concat(isMarket(Markets.it) ? [RoleEnum.SALES_DEALER] : [])}><ReporterListPage/></RestrictedRoute>} />
							<Route path="/my-reporters/details/:username" element={<RestrictedRoute to={isMarket(Markets.it) ? [RoleEnum.SALES_DEALER] : []}><ReporterDetailsPage /></RestrictedRoute>} />
							<Route path="/my-reporters/:username" element={<RestrictedRoute to={[RoleEnum.SALES_DEALER]}><UserFormPage /></RestrictedRoute>} />
						</>
					)}
					<Route path="/my-reports" element={<RestrictedRoute to={[RoleEnum.SEGNALATORE, RoleEnum.MASTER]}><ReportListPage /></RestrictedRoute>} />
					{isMarket(Markets.it) && <Route path="/contacts" element={<ContactsPage />} />}
					<Route path="/quotations-history" element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL]}><QuotationsHistoryPage /></RestrictedRoute>} />

					<Route
						path="/vehicles/create"
						element={<MotornetCreateVehiclePage />}
					/>
					{/* L'import veicoli da Excel non è disponibile sul mercato ES */}
					{isMarket(Markets.it) && (
						<>
							<Route
								path="/vehicles/resolve"
								element={<RestrictedRoute to={[RoleEnum.MASTER]}><ResolveVehiclePage /></RestrictedRoute>}
							/>
							<Route
									path="/import"
									element={<RestrictedRoute to={[RoleEnum.MASTER, RoleEnum.ADMIN]}><MotornetImportPage /></RestrictedRoute>}
								/>
						</>
					)}
				</Routes>
			</main>
		</div>
	);
}

export default App;
